import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFileContract, FaUserCheck, FaExclamationTriangle, FaCopyright, FaBan } from 'react-icons/fa';
import HeaderLandingPage from '../components/HeaderLandingPage';

const Terms = () => {
  // Initialise dark mode from localStorage or default to false
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('cashsule-theme');
    return savedTheme === 'dark';
  });
  
  // Toggle dark mode and save to localStorage
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('cashsule-theme', newDarkMode ? 'dark' : 'light');
  };
  
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    // Set up dark mode for this component only
    const pageContainer = document.getElementById('terms-page-container');
    
    if (isDarkMode) {
      pageContainer.classList.add('dark');
    } else {
      pageContainer.classList.remove('dark');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark');
    };
  }, [isDarkMode]);

  return (
    <div id="terms-page-container" className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <HeaderLandingPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      {/* Content starts after the header with padding */}
      <div className="pt-16"> {/* Add padding equal to header height */}
        {/* Breadcrumb Navigation */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Link
              to="/"
              className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">Terms of Service</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="flex items-center mb-6">
                <FaFileContract className="h-8 w-8 text-primary-600 mr-3" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>
              </div>
              
              <p className="text-gray-700 dark:text-gray-300 mb-8">
                Last Updated: 18 April 2025
              </p>

              <div className="space-y-10">
                {/* Introduction */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Introduction</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Welcome to Cashsule. These Terms of Service govern your use of our web application and services.
                    By accessing or using Cashsule, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the service.
                  </p>
                </section>

                {/* User Responsibilities */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaUserCheck className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User Responsibilities</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300 mb-3">As a user of Cashsule, you agree to:</p>
                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-4">
                      <li>Provide accurate and complete information when creating your account</li>
                      <li>Maintain the security of your account credentials</li>
                      <li>Take responsibility for all activities that occur under your account</li>
                      <li>Use the service only for lawful purposes and in accordance with these Terms</li>
                      <li>Not enter sensitive banking information or financial credentials</li>
                    </ul>
                    
                    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 p-4 my-4">
                      <p className="text-blue-700 dark:text-blue-300 font-medium">
                        Cashsule is designed to track financial information you enter manually. You should never enter bank login details, account numbers, or other sensitive financial data.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Service Description */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Service Description</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Cashsule is a financial tracking tool that allows you to:
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
                    <li>Track expenses and income</li>
                    <li>Create and manage budgets</li>
                    <li>Monitor recurring payments</li>
                    <li>Generate financial statements</li>
                    <li>Set financial goals</li>
                  </ul>
                </section>

                {/* Limitations */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaExclamationTriangle className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Limitations and Disclaimers</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      Cashsule is provided "as is" and "as available" without warranties of any kind. We do not guarantee that the service will always be available, uninterrupted, or error-free.
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                        Cashsule is not a financial adviser or professional accounting tool. It is designed to help individuals track personal finances, but should not be used for:
                    </p>
                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
                      <li>Professional accounting or tax preparation</li>
                      <li>Financial or investment advice</li>
                      <li>Business accounting or bookkeeping</li>
                      <li>Banking or financial transactions</li>
                    </ul>
                  </div>
                </section>

                {/* Data Accuracy */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Data Accuracy</h2>
                  <p className="text-gray-700 dark:text-gray-300">
                    The accuracy of the information in your Cashsule account depends on the data you enter. We are not responsible for errors, inaccuracies, or omissions in your financial records resulting from manual data entry.
                  </p>
                </section>

                {/* Prohibited Uses */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaBan className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Prohibited Uses</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300 mb-3">You agree not to use Cashsule to:</p>
                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
                      <li>Violate any laws or regulations</li>
                      <li>Infringe on the rights of others</li>
                      <li>Store or transmit harmful code or malware</li>
                      <li>Attempt to gain unauthorised access to any part of the service</li>
                      <li>Use the service for money laundering or other illegal financial activities</li>
                    </ul>
                  </div>
                </section>

                {/* Intellectual Property */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaCopyright className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Intellectual Property</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300">
                      Cashsule and its original content, features, and functionality are owned by us and are protected by international copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works of any part of the service without our prior written consent.
                    </p>
                  </div>
                </section>

                {/* Termination */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Termination</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    We reserve the right to terminate or suspend your account and access to the service immediately, without prior notice or liability, for any reason whatsoever, including but not limited to:
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
                    <li>Violation of our community guidelines, including harassment or abusive behaviour towards other users</li>
                    <li>Misuse of the service for illegal activities or financial fraud</li>
                    <li>Attempting to manipulate or exploit the system</li>
                    <li>Sharing or distributing harmful content</li>
                    <li>Unauthorised access to other users' accounts or data</li>
                    <li>Any other activities that we determine to be harmful to our community or service</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Upon termination or suspension:
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
                    <li>Your right to use the service will immediately cease</li>
                    <li>If you have a premium subscription, it will be automatically cancelled</li>
                    <li>No refunds will be provided for any unused portion of your premium subscription</li>
                    <li>We reserve the right to delete or retain your account data at our discretion</li>
                  </ul>
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 mt-4">
                    <p className="text-yellow-700 dark:text-yellow-300">
                      All decisions regarding account termination or suspension are final and at the sole discretion of Cashsule. We are not obliged to provide detailed explanations for our decisions.
                    </p>
                  </div>
                </section>

                {/* Changes to Terms */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Changes to These Terms</h2>
                  <p className="text-gray-700 dark:text-gray-300">
                    We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                  </p>
                </section>

                {/* Contact */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Contact Us</h2>
                  <p className="text-gray-700 dark:text-gray-300">
                    If you have any questions about these Terms, please contact us at{' '}
                    <a 
                      href="mailto:cashsule.app@gmail.com" 
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      cashsule.app@gmail.com
                    </a>.
                  </p>
                </section>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">Privacy Policy</Link> | 
              <Link 
                to="/" 
                className="text-primary-600 dark:text-primary-400 hover:underline ml-2"
              >
                Back to Home
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Terms; 